import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

type Row = { user_id: string; day: string; score: number; note: string | null; pillar: string | null };
type Profile = { id: string; email: string | null; display_name: string | null };

const iso = (d: Date) => d.toISOString().slice(0, 10);
const streakOf = (days: Set<string>) => {
  let n = 0; const d = new Date();
  if (!days.has(iso(d))) d.setDate(d.getDate() - 1);
  while (days.has(iso(d))) { n++; d.setDate(d.getDate() - 1); }
  return n;
};

const AdminAppProgress = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) { setError("Please sign in as an admin."); return; }
      const { data: role } = await supabase.from("user_roles").select("role").eq("user_id", u.user.id).eq("role", "admin").maybeSingle();
      if (!role) { setError("This page is for admins only."); return; }
      const since = new Date(); since.setDate(since.getDate() - 90);
      const { data, error } = await supabase.from("app_checkins")
        .select("user_id,day,score,note,pillar").gte("day", iso(since)).order("day", { ascending: false }).limit(5000);
      if (error) { setError(error.message); return; }
      setRows(data ?? []);
      const ids = [...new Set((data ?? []).map((r) => r.user_id))];
      if (ids.length) {
        const { data: p } = await supabase.from("profiles").select("id,email,display_name").in("id", ids);
        setProfiles(Object.fromEntries((p ?? []).map((x) => [x.id, x])));
      }
    })();
  }, []);

  const people = useMemo(() => {
    const by: Record<string, Row[]> = {};
    rows.forEach((r) => (by[r.user_id] ??= []).push(r));
    const week = iso(new Date(Date.now() - 6 * 86_400_000));
    return Object.entries(by).map(([id, list]) => {
      const days = new Set(list.map((r) => r.day));
      const recent = list.filter((r) => r.day >= week);
      return {
        id, list,
        streak: streakOf(days),
        total: list.length,
        last: list[0]?.day,
        avg7: recent.length ? (recent.reduce((s, r) => s + r.score, 0) / recent.length).toFixed(1) : "—",
      };
    }).sort((a, b) => (b.last ?? "").localeCompare(a.last ?? ""));
  }, [rows]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="container max-w-5xl py-10">
        <Link to="/app" className="text-sm text-muted-foreground underline">← Back to the app</Link>
        <h1 className="font-display uppercase text-3xl mt-3">MAKE CHANGE NOW — Progress</h1>
        <p className="text-muted-foreground text-sm mt-1">Everyone signed in to the app, last 90 days. Click a person to see their check-ins.</p>
        {error && <p className="text-destructive mt-4 text-sm">{error}</p>}
        {!error && people.length === 0 && <p className="mt-8 text-muted-foreground">No saved check-ins yet.</p>}
        <div className="mt-6 border border-border divide-y divide-border">
          {people.map((p) => {
            const prof = profiles[p.id];
            return (
              <div key={p.id}>
                <button type="button" onClick={() => setOpen(open === p.id ? null : p.id)}
                  className="w-full grid grid-cols-2 md:grid-cols-5 gap-2 text-left px-4 py-3 hover:bg-muted/40 text-sm">
                  <span className="font-medium col-span-2 md:col-span-1 truncate">{prof?.display_name || prof?.email || p.id.slice(0, 8)}<span className="block text-xs text-muted-foreground truncate">{prof?.email}</span></span>
                  <span>Streak: <b>{p.streak}</b></span>
                  <span>Check-ins: <b>{p.total}</b></span>
                  <span>7-day avg: <b>{p.avg7}</b></span>
                  <span>Last: {p.last}</span>
                </button>
                {open === p.id && (
                  <ul className="px-4 pb-4 space-y-1 text-sm">
                    {p.list.slice(0, 60).map((r) => (
                      <li key={r.day} className="flex gap-3"><span className="w-24 text-muted-foreground">{r.day}</span><span className="w-10">{r.score}/5</span><span className="w-32 text-muted-foreground">{r.pillar}</span><span className="flex-1">{r.note}</span></li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminAppProgress;
